import { Button, DatePicker, Input, InputRef, Space, Table, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { Fragment, ReactNode, useMemo, useRef } from 'react';
import moment, { Moment } from 'moment-timezone';
import { Link, useSearchParams } from 'react-router-dom';
import { BaseEntity } from 'app/types/baseEntity';
import { replaceVariables } from 'utils/url';
import { formatDate, formats } from 'utils/datetime';
import { ColumnType } from 'antd/lib/table';
import { ColumnFilterItem, ExpandableConfig, FilterConfirmProps, SorterResult, TablePaginationConfig, TableRowSelection } from 'antd/lib/table/interface';
import { SearchOutlined } from '@ant-design/icons';
import { FilterValue } from 'antd/es/table/interface';
import { TooltipButton } from 'components/TooltipButton/TooltipButton';

import type { ColumnsType } from 'antd/es/table';
import { globalActions, selectors as globalSelectors } from 'app/services/global';

import './styles.less';
import _ from 'underscore';
import sort from 'utils/sort';
import { useScreenBreakpoint } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import Ellipsis from 'components/Ellipsis/Ellipsis';

export enum ColumnTypes {
  TEXT,
  ACTIONS,
  LINK,
  DATE,
  DATETIME,
  TAG,
  CUSTOM
}

export type Column = {
  key: string,
  title: string,
  type: ColumnTypes,
  hasSearch?: boolean,
  hasSort?: boolean,
  link?: string | ((entity: any) => string | undefined),
  actions?: (ActionButton | ((entity: any) => ReactNode))[],
  tags?: Tag[],
  defaultOrder?: 'ascend' | 'descend',
  useEllipsis?: boolean;
  renderAs?: (entity: any) => ReactNode;
  hasDateFilter?: boolean;
  filters?: ColumnFilterItem[];
  onFilter?: (value: string | number | boolean, entity: any) => boolean;
  width?: number;
}

export interface ActionButton {
  icon: ReactNode;
  onClick: any;
  toolTip: string;
  hidden?: (entity: any) => boolean,
}

type Tag = {
  key: string,
  value: any,
  color: string,
  message: string,
}

const pageSizes = [ 50, 100, 250 ] as const;

const DATE_FILTER_KEY_FORMAT = 'YYYY-MM-DD';
const DATE_FILTER_KEY_SEPARATOR = '_';

const parseDateFilterKey = (key?: string): [Moment | null, Moment | null] | null => {
  if (!key) return null;
  const [ from, to ] = key.split(DATE_FILTER_KEY_SEPARATOR);
  return [ from ? moment(from, DATE_FILTER_KEY_FORMAT) : null, to ? moment(to, DATE_FILTER_KEY_FORMAT) : null ];
};

type Props<T> = {
  data: T[] | undefined,
  columns: Column[],
  isLoading: boolean,
  expandable?: ExpandableConfig<T> | undefined,
  pagination?: boolean,
  defaultSort?: string,
  nested?: boolean,
  handleRowClick?: (data: T) => void,
  defaultPageSize?: typeof pageSizes[number] | undefined,
  tableKey?: string,
  hasHiddenSort?: boolean,
  mobileSingleColumn?: boolean,
  rowStatusColor?: (record: T) => string | undefined | null,
  selectedRows?: T[],
  onSelectionChange?: (rows: T[]) => void,
};

const DataTable = <T extends BaseEntity>({
  data,
  columns,
  isLoading,
  expandable,
  pagination,
  defaultSort,
  nested,
  handleRowClick,
  defaultPageSize,
  tableKey,
  hasHiddenSort,
  mobileSingleColumn,
  rowStatusColor,
  selectedRows,
  onSelectionChange
}: Props<T>) => {
  const { t } = useTranslation();
  const [ searchParams, setSearchParams ] = useSearchParams();
  const { isMobile } = useScreenBreakpoint();

  const dispatch = useDispatch();
  const savedSort = useSelector(globalSelectors.getTableSort(tableKey));

  const sortedData = useMemo(() => {
    if (!data) return [];

    if (!hasHiddenSort) return data;

    if (savedSort?.columnKey && savedSort?.direction) {
      return [ ...data ].sort((a: any, b: any) => {
        const result = sort.func(a[savedSort.columnKey], b[savedSort.columnKey]);
        return savedSort.direction === 'descend' ? -result : result;
      });
    }

    return data;
  }, [ data, hasHiddenSort, savedSort ]);

  const renderByType = (column: Column) => {
    switch (column.type) {

      case ColumnTypes.TEXT:
        return (text: string) => renderText(column, text);

      case ColumnTypes.ACTIONS:
        return (_: any, entity: T) => renderActionColumn(column, entity);

      case ColumnTypes.LINK:
        return (text: string, entity: T) => renderLinkColumn(text, entity, column.link);

      case ColumnTypes.DATE:
        return (text: Date) => text ? formatDate(text) : '-';

      case ColumnTypes.DATETIME:
        return (text: Date) => text ? formatDate(text, formats.DD_MM_YYYY_HH_mm) : '-';

      case ColumnTypes.TAG:
        return (value: any, entity: T) => value !== null ? column.tags && renderTags(entity, column.tags) : '-';

      case ColumnTypes.CUSTOM:
        return (value: any, entity: T) => value !== null ? column.renderAs && column.renderAs(entity) : '-';
    }
  };

  const renderText = (column: Column, text : string) => {
    const finalText = text || '-';
    return column.useEllipsis ? <Ellipsis text={finalText} /> : finalText;
  };

  const renderTags = (entity: any, tags: Tag[]) => {
    const components: any = [];

    _.forEach(tags, (tag, index) => {
      const value = entity[tag.key];
      const match = value == tag.value;
      if (match) components.push(<Tag key={`${tag.key}-${index}`} color={tag.color}>{tag.message}</Tag>);
    });

    // Keep a small vertical gap when the tags wrap onto several lines in a narrow column.
    return <Space size={[ 0, 4 ]} wrap>{components}</Space>;
  };


  const renderActionColumn = (column: Column, entity: T) => {
    if (!column.actions || !column.actions.length) return null;

    const { actions } = column;

    const components = actions.map((action: ActionButton | ((entity: any) => ReactNode), index: number) => {
      if (typeof action === 'function') {
        const children = (action as (entity: any) => ReactNode)(entity);
        return <Fragment key={`action-${index}-${entity.Id}`}>{children}</Fragment>;
      }
      const button = action as ActionButton;

      if (button.hidden && button.hidden(entity)) return null;

      return <TooltipButton
        buttonProps={{
          icon: button.icon,
          onClick: () => button.onClick(entity),
          type: 'link'
        }}
        title={button.toolTip || ''}
        minimized={true}
        key={`tooltip-${index}-${entity.Id}`}
      />;
    });

    return (<Space key={`actions-${entity.Id}`} direction='horizontal' size={'small'} onClick={(e) => e.stopPropagation()}>
      {components}
    </Space>);
  };

  const renderLinkColumn = (text: string, entity: T, link: string | ((entity: any) => string | undefined) | undefined) => {
    const url = (typeof link === 'function') ? link(entity) : replaceVariables(link, entity.Id.toString()) ?? '';
    if (!url) return text;
    return <Link onClick={(e) => e.stopPropagation()} to={url}>{text}</Link>;
  };

  const getColumnSearchProps = <T extends BaseEntity>(column: Column): ColumnType<T> => {
    if (!column.hasSearch) return {};

    const dataIndex = column.key as keyof T;
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (confirm: (param?: FilterConfirmProps) => void) => {
      confirm({ closeDropdown: true });
    };

    const handleReset = (clearFilters: () => void, confirm: (param?: FilterConfirmProps) => void) => {
      clearFilters();
      confirm({ closeDropdown: true });
    };

    return {
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            className='input'
            ref={searchInput}
            placeholder={t('common.searchplaceholder', { key: column.title.toLowerCase() })}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [ e.target.value ] : [])}
            onPressEnter={() => handleSearch(confirm)}

          />
          <Space>
            <Button
              className='button'
              type="primary"
              onClick={() => handleSearch(confirm)}
              icon={<SearchOutlined />}
              size="small"
            >
              {t('common.search')}
            </Button>
            <Button
              className='button'
              onClick={() => clearFilters && handleReset(clearFilters, confirm)}
              size="small"
            >
              {t('common.reset')}
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) =>
        (record[dataIndex] as unknown as string || '')
          .toString()
          .toLowerCase()
          .includes((value as string).toLowerCase()),
    };
  };

  const getColumnDateFilterProps = <T extends BaseEntity>(column: Column): ColumnType<T> => {
    if (!column.hasDateFilter) return {};

    const dataIndex = column.key as keyof T;

    const handleSearch = (confirm: (param?: FilterConfirmProps) => void) => {
      confirm({ closeDropdown: true });
    };

    const handleReset = (clearFilters: () => void, confirm: (param?: FilterConfirmProps) => void) => {
      clearFilters();
      confirm({ closeDropdown: true });
    };

    return {
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <DatePicker.RangePicker
            style={{ display: 'flex', marginBottom: 8 }}
            format={formats.DD_MM_YYYY}
            inputReadOnly={isMobile}
            value={parseDateFilterKey(selectedKeys[0]?.toString())}
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
            onChange={(dates) => {
              const from = dates?.[0];
              const to = dates?.[1];
              setSelectedKeys(from && to ? [ `${from.format(DATE_FILTER_KEY_FORMAT)}${DATE_FILTER_KEY_SEPARATOR}${to.format(DATE_FILTER_KEY_FORMAT)}` ] : []);
            }}
          />
          <Space>
            <Button
              className='button'
              type="primary"
              onClick={() => handleSearch(confirm)}
              icon={<SearchOutlined />}
              size="small"
            >
              {t('common.search')}
            </Button>
            <Button
              className='button'
              onClick={() => clearFilters && handleReset(clearFilters, confirm)}
              size="small"
            >
              {t('common.reset')}
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => {
        const date = record[dataIndex] as unknown as Date | string | undefined;
        if (!date) return false;

        const range = parseDateFilterKey(value.toString());
        if (!range) return true;

        const [ from, to ] = range;
        const current = moment(date);
        return (!from || current.isSameOrAfter(from, 'day')) && (!to || current.isSameOrBefore(to, 'day'));
      },
    };
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[],
  ) => {
    if (nested) return;

    const params: any = {};

    if (pagination.current) params['page'] = pagination.current.toString();

    const sorters = Array.isArray(sorter) ? sorter : [ sorter ];

    _.each(sorters, s => {
      if (s.column && s.field) {
        params['orderby'] = s.field.toString();
        params['dir'] = s.order;

        if (tableKey) {
          if (s.field && s.order) {
            dispatch(globalActions.setTableSort({
              tableKey,
              columnKey: s.field.toString(),
              direction: s.order,
            }));
          } else {
            dispatch(globalActions.clearTableSort({ tableKey }));
          }
        }
      }

      _.each(filters, (value, key) => {
        if (value) params[key] = value;
      });
    });

    setSearchParams(params, { replace: true });
  };

  const initialFilter = _.chain(columns)
    .filter(c => c.hasSearch || c.hasDateFilter || !!c.filters)
    .map(c => ({ key: c.key, values: searchParams.getAll(c.key) }))
    .filter(kv => kv.values.length > 0)
    .value();

  const initialSort = searchParams.get('orderby') ?? savedSort?.columnKey;
  const initialDirection = searchParams.get('dir') ?? savedSort?.direction;
  const initialPage = searchParams.get('page') ? parseInt(searchParams.get('page') || '1', 10) : undefined;

  const getInitialFilter = (column: Column) => {
    const values = _.find(initialFilter, (f) => f.key === column.key)?.values ?? [];
    return values.length > 0 ? values : null;
  };

  const getInitialSort = (column: Column) => {
    const hasSort = column.key === (initialSort || defaultSort);
    if (!hasSort) return null;
    if (column.defaultOrder && !initialDirection) return column.defaultOrder;
    return initialDirection === 'descend' ? 'descend' : 'ascend';
  };

  const mappedColumns: ColumnsType<T> = columns.map((column) => ({
    title: column.title,
    dataIndex: column.key,
    render: renderByType(column),
    defaultSortOrder: getInitialSort(column),
    sorter: column.hasSort ? (a: any, b: any) => sort.func(a[column.key], b[column.key]) : undefined,
    filteredValue: getInitialFilter(column),
    width: column.width ?? (column.type === ColumnTypes.ACTIONS ? 75 : undefined),
    filters: column.filters,
    onFilter: column.onFilter,
    ...getColumnSearchProps(column),
    ...getColumnDateFilterProps(column),
  }));

  const hasPagination = pagination === undefined || pagination;

  const mobileColumns = mobileSingleColumn ? [ mappedColumns[0] ] : [ mappedColumns[0], mappedColumns[mappedColumns.length - 1] ];
  const tableColumns = isMobile ? mobileColumns : mappedColumns;

  // The rows the column filters leave, filtered the way antd does it.
  const getFilteredData = () => tableColumns.reduce((rows, column) => {
    const { filteredValue, onFilter } = column;
    return filteredValue && onFilter ? rows.filter(row => filteredValue.some(value => onFilter(value, row))) : rows;
  }, sortedData);

  // antd's own "select all" replaces the selection, which would drop selected rows the current filters hide.
  const rowSelection: TableRowSelection<T> | undefined = selectedRows && onSelectionChange && {
    selectedRowKeys: selectedRows.map(row => row.Id),
    onChange: (__, rows) => onSelectionChange(rows),
    selections: [
      {
        key: 'all',
        text: t('components.datatable.selectall'),
        onSelect: () => onSelectionChange(_.uniq([ ...selectedRows, ...getFilteredData() ], false, row => row.Id)),
      },
      {
        key: 'none',
        text: t('components.datatable.selectnone'),
        onSelect: () => onSelectionChange([]),
      },
    ],
  };

  return <Table
    rowKey={'Id'}
    rowSelection={rowSelection}
    pagination={hasPagination && {
      current: initialPage,
      showSizeChanger: true,
      pageSizeOptions: [ ...pageSizes ],
      defaultPageSize: defaultPageSize ?? pageSizes[0]
    }}
    loading={isLoading}
    columns={tableColumns}
    dataSource={sortedData}
    onChange={handleTableChange}
    expandable={expandable}
    size={'middle'}
    onRow={(record) => {
      const color = rowStatusColor && rowStatusColor(record);

      return {
        onClick: () => handleRowClick && handleRowClick(record),
        style: color ? { boxShadow: `inset 4px 0 0 0 ${color}` } : undefined,
      };
    }}
  />;
};

export { DataTable };